import React, { useCallback, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { DossierResponseDto } from 'lib_api/lib/api/gen';

import { useSubmitSortieVehicule } from 'hooks/dossiers/useSubmitSortieVehicule';
import { useVerificationLogic } from 'hooks/sortieVehicule/useVerificationLogic';
import { SortieVehicule } from 'types/SortieVehicule';
import { extractNonnullDossierIdOrThrow } from 'utils/dossierUtils';

import FormWrapper from '../../../../../../components/WrappedComponents/Form/FormWrapper/FormWrapper';
import { useValidateSortieVehiculeForm } from '../utils';

import VerificationPreSortie from './VerificationPreSortie/VerificationPreSortie';
import VerificationPreSortieCodeML from './VerificationPreSortie/VerificationPreSortieCodeML/VerificationPreSortieCodeML';
import { VerificationPreSortieCodeMLCheckbox } from './VerificationPreSortie/VerificationPreSortieCodeML/VerificationPreSortieCodeMLCheckbox/VerificationPreSortieCodeMLCheckbox';
import VerificationPreSortieInfosProprietaire from './VerificationPreSortie/VerificationPreSortieInfoProprietaire/VerificationPreSortieInfoProprietaire';
import { VerificationPreSortieInfoProprietaireCheckbox } from './VerificationPreSortie/VerificationPreSortieInfoProprietaire/VerificationPreSortieInfoProprietaireCheckbox/VerificationPreSortieInfoProprietaireCheckbox';
import { DatePickerSortieParc, SelectMotif } from './Fields';
import {
  generateSortieVehiculeFromDossier,
  generateSortieVehiculeRequestDtoFromSortieVehicule,
} from './utils';

import './SortieVehicule.less';

interface SortieVehiculeProps {
  dossier: DossierResponseDto;
}

const SortieVehiculeForm: React.FC<SortieVehiculeProps> = ({
  dossier,
}: SortieVehiculeProps): React.ReactElement => {
  const [values, setValues] = useState<SortieVehicule>(
    generateSortieVehiculeFromDossier(dossier),
  );

  const dossierId = extractNonnullDossierIdOrThrow(dossier);
  const [shouldRedirect, submitSortieVehicule] = useSubmitSortieVehicule();
  const { isVerificationProprietaireRequired, isVerificationCodeMLRequired } =
    useVerificationLogic(dossier.body?.traitement);

  const shouldDisplayVerificationInfoProprietaireBlock =
    isVerificationProprietaireRequired(values.motifSortieIdCorrelation ?? '');
  const shouldDisplayVerificationCodeMLBlock = isVerificationCodeMLRequired(
    values.motifSortieIdCorrelation ?? '',
  );
  const shouldDisplayVerificationPreSortieBlock =
    shouldDisplayVerificationInfoProprietaireBlock ||
    shouldDisplayVerificationCodeMLBlock;

  const submitForm = useCallback((): Promise<void> => {
    return submitSortieVehicule(values, dossierId);
  }, [values, dossierId, submitSortieVehicule]);

  const [validateForm] = useValidateSortieVehiculeForm(submitForm);

  if (shouldRedirect) {
    return <Navigate to="/gardien-fourriere" />;
  }

  return (
    <FormWrapper
      initialValue={values}
      onValuesChangeCallback={(allValues: SortieVehicule): SortieVehicule => {
        return {
          ...allValues,
          // De mémoire, `allValues.isConfirmedInfosProprietaire` et `allValues.isConfirmedCodeML` sont parfois
          // `undefined` alors qu'ils ne devraient pas l'être d'après le type Typescript. Toujours de mémoire,
          // supprimer l'écrasement de `isConfirmedCodeML` et `isConfirmedInfosProprietaire` provoque un bug qui
          // consiste en un retard de mise à jour du state : le state se met à jour au render qui suit celui attendu.
          isConfirmedCodeML: allValues.isConfirmedCodeML ?? false,
          isConfirmedInfosProprietaire:
            allValues.isConfirmedInfosProprietaire ?? false,
        };
      }}
      id={'SortieVehiculeForm'}
      onSubmit={async (): Promise<void> => {
        return await validateForm(gfController =>
          gfController.validateSortieVehiculeFormUsingPOST(
            dossierId,
            generateSortieVehiculeRequestDtoFromSortieVehicule(values),
          ),
        );
      }}
      onChange={setValues}
      validateButtonTitle={'Confirmer la sortie'}
    >
      {(values, submitButton): React.ReactElement => {
        return (
          <div className="sortie-vehicule-form">
            <DatePickerSortieParc values={values} dossierId={dossierId} />
            <SelectMotif values={values} dossierId={dossierId} />

            {shouldDisplayVerificationPreSortieBlock && (
              <VerificationPreSortie>
                {shouldDisplayVerificationInfoProprietaireBlock && (
                  <VerificationPreSortieInfosProprietaire dossierId={dossierId}>
                    <VerificationPreSortieInfoProprietaireCheckbox
                      dossierId={dossierId}
                      values={values}
                    />
                  </VerificationPreSortieInfosProprietaire>
                )}

                {shouldDisplayVerificationCodeMLBlock && (
                  <VerificationPreSortieCodeML>
                    <VerificationPreSortieCodeMLCheckbox
                      dossierId={dossierId}
                      values={values}
                      codeML={
                        dossier.body?.traitement?.mainLeveeUsager
                          ?.codeVerification
                      }
                    />
                  </VerificationPreSortieCodeML>
                )}
              </VerificationPreSortie>
            )}

            <div className="SubmitWrapper">{submitButton}</div>
          </div>
        );
      }}
    </FormWrapper>
  );
};

export default SortieVehiculeForm;
